import { createReducer, createAction, createSelector } from "@reduxjs/toolkit";
import { call, put, takeLatest } from "redux-saga/effects";
import { fetchTrajectoryAggregate, htpmdVersion } from "../lib/api";

import {
  selectors as mdSearchSelectors,
  actions as mdSearchActions,
  numericRangeFields,
  enumerables,
  availableData,
} from "./mdSearch";

export const actions = {
  request_trajectory_aggregate_data: createAction("REQUEST_TRAJECTORY_AGGREGATE_DATA"),
  receive_trajectory_aggregate_data: createAction("RECEIVE_TRAJECTORY_AGGREGATE_DATA"),
  receive_trajectory_aggregate_data_failed: createAction("RECEIVE_TRAJECTORY_AGGREGATE_DATA_FAILED"),
  update_trajectory_aggregate_brush: createAction("UPDATE_TRAJECTORY_AGGREGATE_PLOT_SELECTION"),
  clear_trajectory_aggregate_brush: createAction("CLEAR_TRAJECTORY_AGGREGATE_PLOT_SELECTION"),
};

export const constants = {
  LOADED: "LOADED",
  LOADING: "LOADING",
  ERROR: "ERROR",
};

const emptyPlotSelection = {
  plotKey: "",
  xKey: "",
  yKey: "",
  yRange: [0, 0],
  xRange: [0, 0],
};

export const initState = {
  loadingState: constants.LOADING,
  trajectoryData: [],
  plotSelection: emptyPlotSelection,
};

export const actionMap = {
  [actions.request_trajectory_aggregate_data]: (state) => {
    return { ...state, loadingState: constants.LOADING };
  },
  [actions.receive_trajectory_aggregate_data]: (state, action) => {
    const { trajectoryData } = action.payload;
    return { ...state, trajectoryData, loadingState: constants.LOADED };
  },
  [actions.receive_trajectory_aggregate_data_failed]: (state) => {
    return { ...state, loadingState: constants.ERROR };
  },
  [actions.update_trajectory_aggregate_brush]: (state, action) => {
    state.plotSelection.plotKey = action.payload.plotKey;
    state.plotSelection.xKey = action.payload.xKey;
    state.plotSelection.yKey = action.payload.yKey;
    state.plotSelection.yRange = action.payload.yRange;
    state.plotSelection.xRange = action.payload.xRange;

    return state;
  },
  [actions.clear_trajectory_aggregate_brush]: (state) => {
    state.plotSelection = emptyPlotSelection;

    return state;
  },
};

export const reducer = createReducer(initState, actionMap);

function everyNumberIsNaNOrInRangeForActiveFilters(searchState, trajectory) {
  return numericRangeFields.every((field) => {
    const { label } = field;

    return (
      searchState[label].checked === false ||
      !Number.isFinite(trajectory[label]) ||
      (trajectory[label] >= searchState[label].filterRange[0] && trajectory[label] <= searchState[label].filterRange[1])
    );
  });
}

function enumerableIsNullOrCheckedForActiveFilters(searchState, trajectory) {
  return enumerables.every((enumerable) => {
    const value = trajectory[enumerable];
    return value === null || searchState[enumerable].checked[value] === true;
  });
}

function availableDataIsSet(searchState, trajectory) {
  return availableData.every((property) => {
    // if filter flag is set to true, check if property is true
    if (searchState[property].checked === true) {
      return trajectory[property] === true;
    }

    return true;
  });
}

const getMDTrajectoriesAggregate = (state) => state.mdTrajectoriesAggregate;
const getMDTrjajectoriesPlotSelection = (state) => getMDTrajectoriesAggregate(state).plotSelection;
const getMDTrajectoriesAggregateData = createSelector(
  [getMDTrajectoriesAggregate],
  ({ trajectoryData }) => trajectoryData
);
const getMDTrajectoriesAggregateDataMatchingSearch = createSelector(
  [getMDTrajectoriesAggregateData, mdSearchSelectors.getMDSearch],
  (trajectoryData, mdSearch) => {
    return trajectoryData.filter((trajectory) => {
      return (
        enumerableIsNullOrCheckedForActiveFilters(mdSearch, trajectory) &&
        everyNumberIsNaNOrInRangeForActiveFilters(mdSearch, trajectory) &&
        availableDataIsSet(mdSearch, trajectory)
      );
    });
  }
);
const empty = {}; // easy referential equality on clear
const getIsSelectedById = createSelector(
  [getMDTrajectoriesAggregateDataMatchingSearch, getMDTrjajectoriesPlotSelection],
  (filteredAggregateData, { plotKey, xKey, yKey, xRange, yRange }) => {
    if (!plotKey) {
      return empty;
    } else {
      const res = filteredAggregateData
        .filter((d) => {
          return d[xKey] >= xRange[0] && d[xKey] <= xRange[1] && d[yKey] >= yRange[0] && d[yKey] <= yRange[1];
        })
        .reduce((acc, { trajectory_id }) => {
          acc[trajectory_id] = true;
          return acc;
        }, {});
      return res;
    }
  }
);

export const selectors = {
  getMDTrajectoriesAggregate,
  getMDTrjajectoriesPlotSelection,
  getMDTrajectoriesAggregateData,
  getMDTrajectoriesAggregateDataMatchingSearch,
  getIsSelectedById,
};

function* onFetchTrajectoryAggregateData(action) {
  try {
    const { accessToken } = action.payload;
    const { data } = yield call(fetchTrajectoryAggregate, {
      htpmd_version: htpmdVersion,
      accessToken,
    });

    const trajectoryData = data.trajectories;
    yield put(actions.receive_trajectory_aggregate_data({ trajectoryData }));
    yield put(mdSearchActions.receive_trajectory_aggregate_data({ trajectoryData }));
  } catch (e) {
    console.error(e);
    yield put(actions.receive_trajectory_aggregate_data_failed());
  }
}

export const sagas = {
  watchEveryRequestTrajectoryAggregateData: function* watchEveryRequestTrajectoryAggregateData() {
    yield takeLatest(actions.request_trajectory_aggregate_data.toString(), onFetchTrajectoryAggregateData);
  },
};
